import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { map, Observable } from 'rxjs';
import { CategoryExpenseFields, CategoryExpenseResult, InvoiceListResponse } from '@celerofinancas/pcp-client-ui';
import {
  CreditCardsTableDataResult,
  CreditCardsTableDataSource,
  CREDIT_CARDS_TABLE_DATA_SOURCE_CONFIG,
  CreditCardsTableItem,
  CreditCardsTablePagination,
  CreditCardsTableQuery,
  CreditCardsUpdateContractedProductPayload,
} from './credit-cards-data-source.token';
import {
  CategoryExpenseDto,
  CreditCardBillDto,
  CreditCardBillsListResponse,
  CreditCardTransactionDto,
  CreditCardTransactionRow,
  CreditCardTransactionsQuery,
  CreditCardTransactionsResponse,
  CreditCardTransactionsResult,
  isCreditTransaction,
  isRefundTransaction,
  LIFECYCLE_STATUS_MAP,
  normalizePaymentType,
  PAYMENT_TYPE_LABEL,
  TotalExpensesPerCategoryResponse,
} from './credit-cards-data-interface';

/**
 * Invoice item augmented with the backing bill identifier.
 */
export type InvoiceListItem = InvoiceListResponse & { bill_id: string };

/**
 * Number of billing cycles requested when listing bills.
 *
 * The BFF (celerov2-api-client) forwards this as `window_size` to the core
 * gRPC sliding-window query, which is centered on the open bill. The default
 * (12) only reaches ~5 past cycles, so older bills (e.g. jul/2025) fall outside
 * the window and the response comes back with `has_more: true`. A wider window
 * covers a realistic statement history without inflating the payload, since the
 * core only returns bills that actually exist within the window. See 86e1xkck0.
 */
const BILLS_WINDOW_SIZE = 48;

/**
 * Default HTTP implementation for credit cards table data source.
 */
@Injectable({ providedIn: 'root' })
export class CreditCardsHttpDataSourceService implements CreditCardsTableDataSource {
  /**
   * HTTP client.
   */
  private readonly http = inject(HttpClient);

  /**
   * Data source config.
   */
  private readonly config = inject(CREDIT_CARDS_TABLE_DATA_SOURCE_CONFIG);

  /**
   * Lists credit cards table rows.
   *
   * @param query query options
   * @returns normalized table data
   */
  public list(query: CreditCardsTableQuery): Observable<CreditCardsTableDataResult> {
    const requestPayload = this.getRequestPayload(query);
    const request$ =
      this.config.method === 'GET'
        ? this.http.get<unknown>(this.getRequestUrl(this.config.endpoint), {
            params: this.buildHttpParams(requestPayload),
          })
        : this.http.post<unknown>(this.getRequestUrl(this.config.endpoint), requestPayload);

    return request$.pipe(map((response: unknown) => this.mapResponse(response, query)));
  }

  /**
   * Lists bills for a credit card account.
   *
   * @param creditCardAccountId credit card account identifier (pk_core of the account)
   * @returns observable of mapped invoice list
   */
  public listBills(creditCardAccountId: string | number): Observable<InvoiceListItem[]> {
    const params = new HttpParams()
      .set('credit_card_account_id', String(creditCardAccountId))
      .set('window_size', BILLS_WINDOW_SIZE);
    return this.http
      .get<CreditCardBillsListResponse>(this.getRequestUrl('/v2/credit-cards/bills/list'), {
        params,
      })
      .pipe(map((response) => (response?.results ?? []).map(this.mapBillToInvoice)));
  }

  /**
   * Maps a credit card bill to an invoice list response.
   *
   * @param bill credit card bill
   * @param index index of the bill in the list
   * @returns mapped invoice list response
   */
  private readonly mapBillToInvoice = (
    bill: CreditCardBillDto,
    index: number,
  ): InvoiceListItem => {
    const month = String(bill.reference_month).padStart(2, '0');
    return {
      id: index,
      bill_id: bill.pk_core,
      company_id: 0,
      due_at: bill.due_date?.trim() || `${bill.reference_year}-${month}-01`,
      value: bill.bill_total_amount ? parseFloat(bill.bill_total_amount.amount) : undefined,
      minimum_payment: bill.bill_minimum_amount
        ? parseFloat(bill.bill_minimum_amount.amount)
        : undefined,
      status: LIFECYCLE_STATUS_MAP[bill.lifecycle_status] ?? 0,
      year_month: `${bill.reference_year}-${month}`,
    };
  };

  /**
   * Fetches expense categories breakdown for the given bill.
   *
   * @param billId bill identifier (pk_core)
   * @param creditCardAccountId credit card account identifier (required by BFF/gRPC)
   * @returns observable of category expense result
   */
  public listCategories(
    billId: string,
    creditCardAccountId: string,
  ): Observable<CategoryExpenseResult> {
    const httpParams = new HttpParams()
      .set('credit_card_account_id', String(creditCardAccountId))
      .set('bill_id', billId);

    return this.http
      .get<TotalExpensesPerCategoryResponse>(
        this.getRequestUrl('/v2/credit-cards/bills/expenses/categories'),
        { params: httpParams },
      )
      .pipe(map((response) => this.mapCategoriesResponse(response)));
  }

  /**
   * Maps the backend categories response to the chart-friendly shape.
   *
   * @param response backend response
   * @returns normalized CategoryExpenseResult
   */
  private mapCategoriesResponse(
    response: TotalExpensesPerCategoryResponse | null | undefined,
  ): CategoryExpenseResult {
    const expenses: CategoryExpenseDto[] = response?.expenses ?? [];

    const categories: CategoryExpenseFields[] = expenses.map((expense: CategoryExpenseDto) => ({
      name: expense.category_name ?? '',
      value: parseFloat(expense.total_amount?.amount ?? '0') || 0,
      percent: Math.round((expense.percentage ?? 0) * 100 * 10) / 10,
    }));

    const total_value = categories.reduce((sum, category) => sum + category.value, 0);

    return { total_value, categories };
  }

  /**
   * Lists paginated transactions for a given bill.
   *
   * @param query bill id and pagination options
   * @returns observable of normalized transactions result
   */
  public listTransactions(
    query: CreditCardTransactionsQuery,
  ): Observable<CreditCardTransactionsResult> {
    const params = new HttpParams()
      .set('bill_id', query.billId)
      .set('page', String(query.page))
      .set('page_size', String(query.pageSize));

    return this.http
      .get<CreditCardTransactionsResponse>(
        this.getRequestUrl('/v2/credit-cards/transactions/list'),
        { params },
      )
      .pipe(map((response) => this.mapTransactionsResponse(response, query)));
  }

  /**
   * Maps the backend transactions response to the normalized result.
   *
   * @param response backend response
   * @param query query options (used as pagination fallback)
   * @returns normalized transactions result
   */
  private mapTransactionsResponse(
    response: CreditCardTransactionsResponse | null | undefined,
    query: CreditCardTransactionsQuery,
  ): CreditCardTransactionsResult {
    const results = response?.results ?? [];
    const items = results.map(this.mapTransactionRow);
    const totalItems = response?.count ?? items.length;
    const page = response?.page ?? query.page;
    const offset = query.pageSize;
    const totalPages = Math.max(1, Math.ceil(totalItems / Math.max(1, offset)));

    return { items, pagination: { page, offset, totalItems, totalPages } };
  }

  /**
   * Maps a single transaction DTO to a flat table row.
   *
   * @param dto transaction DTO
   * @returns table row
   */
  private readonly mapTransactionRow = (
    dto: CreditCardTransactionDto,
  ): CreditCardTransactionRow => {
    // Refunds (estornos) carry no payment modality, instalment or category, so
    // the three classification columns must all render as "–" regardless of
    // what the backend fills in (card 86e1x1n6q).
    const refund = isRefundTransaction(dto);

    const parcela =
      !refund && dto.current_instalment && dto.total_instalments
        ? `${dto.current_instalment}/${dto.total_instalments}`
        : '–';

    // "Tipo de transação" is the payment modality (À vista / Parcelada), which
    // comes from payment_type — NOT transaction_type (the entry's nature:
    // purchase/fee/payment). Unknown/empty modalities (and refunds) render "–".
    const tipo = refund
      ? '–'
      : PAYMENT_TYPE_LABEL[normalizePaymentType(dto.payment_type)] ?? '–';

    const categoria = refund ? '–' : dto.merchant_category?.category?.trim() || '–';

    return {
      lancamento: dto.transaction_name?.trim() || '–',
      parcela,
      tipo,
      categoria,
      realizadoEm: this.formatTransactionDate(dto.transaction_date_time),
      valor: this.formatTransactionAmount(dto),
    };
  };

  /**
   * Formats an ISO date-time into dd/MM/yyyy, or "–" when absent/invalid.
   *
   * @param value ISO date-time string
   * @returns formatted date
   */
  private formatTransactionDate(value?: string): string {
    if (!value) {
      return '–';
    }

    // Display the calendar date exactly as the backend wrote it. Using the
    // literal ISO date portion avoids the off-by-one shift that new Date(value)
    // + local-timezone formatting causes for UTC ("Z") or date-only timestamps.
    const isoDate = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);

    if (isoDate) {
      const [, year, month, day] = isoDate;
      return `${day}/${month}/${year}`;
    }

    const date = new Date(value);

    return Number.isNaN(date.getTime()) ? '–' : new Intl.DateTimeFormat('pt-BR').format(date);
  }

  /**
   * Formats the transaction amount as a neutral BRL string. Credit (refund)
   * entries are prefixed with a minus sign; the design applies no
   * positive/negative coloring, so the value is returned as plain text.
   *
   * @param dto transaction DTO
   * @returns formatted amount ("R$ 48,90" / "- R$ 27,75") or "–" when not parseable
   */
  private formatTransactionAmount(dto: CreditCardTransactionDto): string {
    const raw = parseFloat(dto.brazilian_amount?.amount ?? '');

    if (!Number.isFinite(raw)) {
      return '–';
    }

    const formatted = new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(Math.abs(raw));

    return isCreditTransaction(dto) ? `- ${formatted}` : formatted;
  }

  /**
   * Updates contracted product name.
   *
   * @param payload update payload
   * @returns request observable
   */
  public updateContractedProduct(
    payload: CreditCardsUpdateContractedProductPayload,
  ): Observable<unknown> {
    const requestPayload = this.getUpdateRequestPayload(payload);
    const updateEndpoint =
      this.config.updateEndpoint ?? '/v1/multiple-accounts-relationship/update';

    return this.http.put<unknown>(this.getRequestUrl(updateEndpoint), requestPayload);
  }

  /**
   * Builds request payload.
   *
   * @param query query options
   * @returns request payload
   */
  private getRequestPayload(query: CreditCardsTableQuery): unknown {
    if (this.config.buildRequest) {
      return this.config.buildRequest(query);
    }

    return {
      cnpj: query.cnpj,
      page: query.page,
      offset: query.offset,
      search: query.search,
    };
  }

  /**
   * Builds update payload.
   *
   * @param payload update payload
   * @returns request payload
   */
  private getUpdateRequestPayload(payload: CreditCardsUpdateContractedProductPayload): unknown {
    if (this.config.buildUpdateRequest) {
      return this.config.buildUpdateRequest(payload);
    }

    return payload;
  }

  /**
   * Builds request URL.
   *
   * @returns complete endpoint url
   */
  private getRequestUrl(endpoint: string): string {
    const baseUrl = this.config.baseUrl.replace(/\/$/, '');
    const normalizedEndpoint = endpoint.replace(/^\//, '');

    return `${baseUrl}/${normalizedEndpoint}`;
  }

  /**
   * Converts generic payload object to HttpParams.
   *
   * @param payload payload object
   * @returns HttpParams object
   */
  private buildHttpParams(payload: unknown): HttpParams {
    if (!this.isRecord(payload)) {
      return new HttpParams();
    }

    let params = new HttpParams();

    Object.entries(payload).forEach(([key, value]) => {
      if (value === null || value === undefined || value === '') {
        return;
      }

      params = params.set(key, String(value));
    });

    return params;
  }

  /**
   * Maps raw backend response to normalized table response.
   *
   * @param response backend response
   * @param query query options
   * @returns normalized response
   */
  private mapResponse(response: unknown, query: CreditCardsTableQuery): CreditCardsTableDataResult {
    if (this.config.mapResponse) {
      return this.config.mapResponse(response, query);
    }

    return this.mapDefaultResponse(response, query);
  }

  /**
   * Applies default generic mapping strategy for unknown backend payloads.
   *
   * @param response backend response
   * @param query query options
   * @returns normalized response
   */
  private mapDefaultResponse(
    response: unknown,
    query: CreditCardsTableQuery,
  ): CreditCardsTableDataResult {
    const responseRecord = this.toRecord(response);
    const extractedRows = this.extractRows(response);
    const normalizedRows = extractedRows.map((row: Record<string, unknown>) => this.mapRow(row));
    const hasServerPagination = this.hasServerPaginationMetadata(responseRecord);

    const page = this.resolvePage(responseRecord, query.page);
    const offset = this.resolveOffset(responseRecord, query.offset);
    const totalItems = this.resolveTotalItems(responseRecord, normalizedRows.length);
    const totalPages = this.resolveTotalPages(responseRecord, totalItems, offset);

    const paginatedRows = hasServerPagination
      ? normalizedRows
      : this.paginateRows(normalizedRows, page, offset);

    const resolvedTotalItems = hasServerPagination ? totalItems : normalizedRows.length;
    const resolvedTotalPages = hasServerPagination
      ? totalPages
      : Math.max(1, Math.ceil(resolvedTotalItems / offset));

    const pagination: CreditCardsTablePagination = {
      page,
      offset,
      totalItems: resolvedTotalItems,
      totalPages: resolvedTotalPages,
    };

    return {
      items: paginatedRows,
      pagination,
      lastUpdated: this.resolveString(responseRecord, ['lastUpdated', 'last_updated']),
    };
  }

  /**
   * Maps unknown row payload to table row.
   *
   * @param row raw row payload
   * @returns normalized row
   */
  private mapRow(row: Record<string, unknown>): CreditCardsTableItem {
    return {
      id: this.resolveString(row, ['id', 'account_id', 'relationship_id']) || undefined,
      bank:
        this.resolveString(row, [
          'bank',
          'institutionName',
          'institution_name',
          'issuer_bank_name',
          'issuer_bank_number',
        ]) || '-',
      bankCode:
        this.resolveString(row, ['issuer_bank_number', 'bankCode', 'bank_code']) || undefined,
      institutionCnpj:
        this.resolveString(row, ['company_cnpj', 'institution_cnpj', 'institutionCnpj']) ||
        undefined,
      brand: this.resolveString(row, ['card_brand', 'brand', 'flag']) || '-',
      contractedProduct:
        this.resolveString(row, [
          'product_tier',
          'contracted_product',
          'contractedProduct',
          'productName',
        ]) || '-',
      totalLimit: this.resolveNumber(row, ['total_limit', 'totalLimit']),
      usedLimit: this.resolveNumber(row, ['used_limit', 'usedLimit']),
      availableLimit: this.resolveNumber(row, ['available_limit', 'availableLimit']),
      isLimitFlexible: row['is_limit_flexible'] === true || row['isLimitFlexible'] === true,
    };
  }

  /**
   * Extracts rows from unknown payload shape.
   *
   * @param response backend response
   * @returns array of raw rows
   */
  private extractRows(response: unknown): Record<string, unknown>[] {
    if (Array.isArray(response)) {
      return response.filter((item: unknown) => this.isRecord(item));
    }

    const responseRecord = this.toRecord(response);

    if (!responseRecord) {
      return [];
    }

    const directList = this.getListFromRecord(responseRecord, [
      'results',
      'items',
      'rows',
      'data',
      'relationships',
    ]);

    if (directList.length) {
      return directList;
    }

    const nestedData = this.toRecord(responseRecord['data']);

    if (!nestedData) {
      return [];
    }

    return this.getListFromRecord(nestedData, ['results', 'items', 'rows']);
  }

  /**
   * Gets first matching list from a record.
   *
   * @param record source record
   * @param keys candidate keys
   * @returns list of row records
   */
  private getListFromRecord(
    record: Record<string, unknown>,
    keys: string[],
  ): Record<string, unknown>[] {
    for (const key of keys) {
      const value = record[key];

      if (!Array.isArray(value)) {
        continue;
      }

      return value.filter((item: unknown) => this.isRecord(item));
    }

    return [];
  }

  /**
   * Applies client-side pagination when backend response is not paginated.
   *
   * @param rows normalized rows
   * @param page current page
   * @param offset page size
   * @returns paginated rows
   */
  private paginateRows(
    rows: CreditCardsTableItem[],
    page: number,
    offset: number,
  ): CreditCardsTableItem[] {
    const safePage = Math.max(1, page);
    const safeOffset = Math.max(1, offset);
    const start = (safePage - 1) * safeOffset;

    return rows.slice(start, start + safeOffset);
  }

  /**
   * Resolves pagination metadata availability.
   *
   * @param responseRecord response record
   * @returns true when metadata exists
   */
  private hasServerPaginationMetadata(responseRecord: Record<string, unknown> | null): boolean {
    if (!responseRecord) {
      return false;
    }

    if (this.isRecord(responseRecord['pagination'])) {
      return true;
    }

    return (
      this.resolveNumber(responseRecord, ['count', 'total', 'total_items', 'totalItems']) !==
        null ||
      this.resolveNumber(responseRecord, ['total_pages', 'totalPages', 'total_page']) !== null ||
      this.resolveNumber(responseRecord, ['page', 'current_page']) !== null
    );
  }

  /**
   * Resolves current page.
   *
   * @param responseRecord response record
   * @param fallback fallback page
   * @returns page value
   */
  private resolvePage(responseRecord: Record<string, unknown> | null, fallback: number): number {
    if (!responseRecord) {
      return Math.max(1, fallback);
    }

    const pagination = this.toRecord(responseRecord['pagination']);
    const value =
      this.resolveNumber(pagination, ['page', 'currentPage']) ??
      this.resolveNumber(responseRecord, ['page', 'current_page']);

    return Math.max(1, value ?? fallback);
  }

  /**
   * Resolves page size.
   *
   * @param responseRecord response record
   * @param fallback fallback offset
   * @returns offset value
   */
  private resolveOffset(responseRecord: Record<string, unknown> | null, fallback: number): number {
    if (!responseRecord) {
      return Math.max(1, fallback);
    }

    const pagination = this.toRecord(responseRecord['pagination']);
    const value =
      this.resolveNumber(pagination, ['offset', 'pageSize', 'page_size', 'perPage']) ??
      this.resolveNumber(responseRecord, ['offset', 'page_size', 'per_page']);

    return Math.max(1, value ?? fallback);
  }

  /**
   * Resolves total items count.
   *
   * @param responseRecord response record
   * @param fallback fallback total
   * @returns total items
   */
  private resolveTotalItems(
    responseRecord: Record<string, unknown> | null,
    fallback: number,
  ): number {
    if (!responseRecord) {
      return fallback;
    }

    const pagination = this.toRecord(responseRecord['pagination']);
    const value =
      this.resolveNumber(pagination, ['totalItems', 'total', 'count', 'total_count']) ??
      this.resolveNumber(responseRecord, ['count', 'total', 'total_items', 'totalItems']);

    return Math.max(0, value ?? fallback);
  }

  /**
   * Resolves total pages.
   *
   * @param responseRecord response record
   * @param totalItems total items
   * @param offset page size
   * @returns total pages
   */
  private resolveTotalPages(
    responseRecord: Record<string, unknown> | null,
    totalItems: number,
    offset: number,
  ): number {
    if (!responseRecord) {
      return Math.max(1, Math.ceil(totalItems / Math.max(1, offset)));
    }

    const pagination = this.toRecord(responseRecord['pagination']);
    const value =
      this.resolveNumber(pagination, ['totalPages', 'total_pages', 'total_page']) ??
      this.resolveNumber(responseRecord, ['total_pages', 'totalPages', 'total_page']);

    if (value !== null) {
      return Math.max(1, value);
    }

    return Math.max(1, Math.ceil(totalItems / Math.max(1, offset)));
  }

  /**
   * Resolves first string-like value from keys.
   *
   * @param source source record
   * @param keys candidate keys
   * @returns string value or null
   */
  private resolveString(source: Record<string, unknown> | null, keys: string[]): string | null {
    if (!source) {
      return null;
    }

    for (const key of keys) {
      const value = source[key];

      if (typeof value === 'string' && !!value.trim()) {
        return value.trim();
      }

      if (typeof value === 'number' && Number.isFinite(value)) {
        return String(value);
      }
    }

    return null;
  }

  /**
   * Resolves first numeric value from keys.
   *
   * @param source source record
   * @param keys candidate keys
   * @returns numeric value or null
   */
  private resolveNumber(source: Record<string, unknown> | null, keys: string[]): number | null {
    if (!source) {
      return null;
    }

    for (const key of keys) {
      const value = source[key];
      const parsedValue = this.toNumber(value);

      if (parsedValue !== null) {
        return parsedValue;
      }
    }

    return null;
  }

  /**
   * Converts unknown value to finite number.
   *
   * @param value unknown value
   * @returns parsed number or null
   */
  private toNumber(value: unknown): number | null {
    if (typeof value === 'number' && Number.isFinite(value)) {
      return value;
    }

    if (typeof value !== 'string') {
      return null;
    }

    const trimmedValue = value.trim();

    if (!trimmedValue) {
      return null;
    }

    const hasComma = trimmedValue.includes(',');
    const hasDot = trimmedValue.includes('.');

    let normalizedValue = trimmedValue;

    if (hasComma && hasDot) {
      normalizedValue =
        trimmedValue.lastIndexOf(',') > trimmedValue.lastIndexOf('.')
          ? trimmedValue.replace(/\./g, '').replace(',', '.')
          : trimmedValue.replace(/,/g, '');
    } else if (hasComma) {
      normalizedValue = trimmedValue.replace(',', '.');
    }

    const sanitizedValue = normalizedValue.replace(/[^0-9.-]/g, '');

    if (!/^-?(?:\d+|\d*\.\d+)$/.test(sanitizedValue)) {
      return null;
    }

    const numericValue = Number(sanitizedValue);

    return Number.isFinite(numericValue) ? numericValue : null;
  }

  /**
   * Checks if the provided value is a non-null object record.
   *
   * @param value unknown value
   * @returns true when value is record
   */
  private isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
  }

  /**
   * Converts unknown value to record.
   *
   * @param value unknown value
   * @returns record or null
   */
  private toRecord(value: unknown): Record<string, unknown> | null {
    return this.isRecord(value) ? value : null;
  }
}
